import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage } from "../../../../../Redux/slice/features/paginationSilce";
import { blockingColumns } from "./BlockingTableData";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../../../Common/DataTable/DataTable";
import DataTableHeader from "../../../../../Common/DataTableHeader/DataTableHeader";
import DataTableFooter from "../../../../../Common/DataTableFooter/DataTableFooter";
import { fetchBlockingHistory } from "../../../../../Redux/slice/features/blockingSlice";
import { fetchHotel } from "../../../../../Redux/slice/features/dropdownHotelSlice";
import UpdateBlockModal from "./UpdateBlockModal";

const BlockingTable = () => {
  const dispatch = useDispatch();
  const { data, status, error } = useSelector((state) => state.blockings);
  const hotels = useSelector((state) => state.hotels.hoteldata);
  const [selectedHotel, setSelectedHotel] = useState("");
  const [modalData, setModalData] = useState({ show: false, data: null });

  const currentPage = useSelector((state) => state.pagination.currentPage);
  const itemsPerPage = useSelector((state) => state.pagination.itemsPerPage);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    bookingRefNo: "",
    customerName: "",
  });

  const navigate = useNavigate();

  const [debouncedCustomer, setDebouncedCustomer] = useState(
    filters.customerName,
  );

  const handleSelectChange = async (e) => {
    setSelectedHotel(e.target.value);
  };

  useEffect(() => {
    dispatch(fetchHotel());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomer(filters.customerName);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.customerName]);

  useEffect(() => {
    const params = {
      hotelId: selectedHotel || 0,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      customerName: debouncedCustomer,
      pageNo: currentPage,
      pageSize: itemsPerPage,
    };

    dispatch(fetchBlockingHistory(params));
  }, [
    dispatch,
    selectedHotel,
    filters.fromDate,
    filters.toDate,
    debouncedCustomer,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [
    dispatch,
    selectedHotel,
    filters.fromDate,
    filters.toDate,
    debouncedCustomer,
  ]);

  const refreshTable = useCallback(() => {
    const params = {
      hotelId: selectedHotel || 0,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      customerName: debouncedCustomer,
      pageNo: currentPage,
      pageSize: itemsPerPage,
    };

    return dispatch(fetchBlockingHistory(params));
  }, [
    dispatch,
    selectedHotel,
    filters.fromDate,
    filters.toDate,
    debouncedCustomer,
    currentPage,
    itemsPerPage,
  ]);

  const columns = useMemo(
    () => blockingColumns(setModalData, navigate, refreshTable),
    [setModalData, navigate, refreshTable],
  );

  const dataT = useMemo(() => {
    if (status !== "fulfilled") return [];
    return data.map((item) => ({
      BookingRefNo: item.ReleaseID || "",
      hotelName: item.PropertyName || "",
      customerName: item.CustomerName,
      blockFromDate: item.BlockFromDate || "",
      blockToDate: item.BlockToDate || "",
      RoomTypeName: item.RoomTypeName || "",
      BlockRooms: item.BlockRooms,
      newBlockRoom: item.NewBlockRoom,
      Remarks: item.Remarks,
      hotelId: item.PropertyID,
      roomTypeId: item.RoomTypeID,
      TotalRooms: item.TotalRooms,
    }));
  }, [data, status]);
  useEffect(() => {
    if (currentPage !== 1) {
      dispatch(setCurrentPage(1));
    }
  }, [dispatch, currentPage, itemsPerPage]);

  const totalRecords = data?.[0]?.TotalCount || 0;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  // console.log('totalPages', totalPages, 'totalRecords', totalRecords);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      dispatch(setCurrentPage(currentPage - 1));
    }
  };

  const clearFilters = () => {
    setSelectedHotel("");

    setFilters({
      fromDate: "",
      toDate: "",
      bookingRefNo: "",
      customerName: "",
    });

    dispatch(setCurrentPage(1));
  };

  return (
    <div className="d-grid gap-4">
      <div className="card admin-surface-card">
        <div className="card-header admin-surface-header">
          <div className="d-flex flex-column flex-md-row justify-content-between gap-2 align-items-md-center">
            <div>
              <h5 className="card-title mb-1">Filters</h5>
              <p className="text-muted mb-0 small">
                Narrow the blocked room list by dates, hotel, or customer.
              </p>
            </div>
          </div>
        </div>
        <div className="card-body admin-surface-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label fw-semibold">From Date</label>
              <input
                type="date"
                className="form-control"
                defaultValue={filters.fromDate}
                onBlur={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    fromDate: e.target.value,
                  }))
                }
              />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold">To Date</label>
              <input
                type="date"
                className="form-control"
                defaultValue={filters.toDate}
                min={filters.fromDate}
                onBlur={(e) => {
                  if (filters.fromDate && e.target.value < filters.fromDate) {
                    alert("To Date cannot be before From Date");
                    return;
                  }

                  setFilters((prev) => ({
                    ...prev,
                    toDate: e.target.value,
                  }));
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Hotel Name</label>
              <select
                className="form-select"
                value={selectedHotel}
                onChange={handleSelectChange}
              >
                <option value="">-- Select Hotel --</option>
                {hotels.map((hotel) => (
                  <option key={hotel.PropertyID} value={hotel.PropertyID}>
                    {hotel.UnitName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Customer Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter customer name"
                value={filters.customerName}
                onChange={(e) =>
                  setFilters({ ...filters, customerName: e.target.value })
                }
              />
            </div>
            <div className="col-md-2 mb-1">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card admin-surface-card">
        <div className="card-header admin-surface-header">
          <div className="d-flex flex-column flex-md-row justify-content-between gap-2 align-items-md-center">
            <div>
              <h5 className="card-title mb-1">
                <p className="mb-0 large">
                  {status === "fulfilled"
                    ? `${totalRecords} record${totalRecords === 1 ? "" : "s"}`
                    : "Loading the latest blocked room data."}
                </p>
              </h5>
            </div>
          </div>
        </div>
        <div className="card-body admin-surface-body">
          {(status === "pending" || status === "loading") && (
            <div className="alert alert-info mb-0">
              Loading blocked rooms...
            </div>
          )}
          {(status === "rejected" || status === "failed") && (
            <div className="alert alert-danger mb-0">Error: {error}</div>
          )}
          {status === "fulfilled" && (
            <>
              <DataTableHeader columns={columns} data={dataT} />
              <div className="admin-table-shell">
                <DataTable
                  columns={columns}
                  data={dataT}
                  enablePagination={false}
                />
              </div>
              <DataTableFooter
                dataT={dataT}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                handleNextPage={handleNextPage}
                handlePreviousPage={handlePreviousPage}
                totalRecords={totalRecords}
              />
            </>
          )}
        </div>
      </div>

      <UpdateBlockModal
        show={modalData.show}
        onClose={() => setModalData({ show: false, data: null })}
        data={modalData.data}
        refreshTable={refreshTable}
      />
    </div>
  );
};

export default BlockingTable;
